import React from 'react';
import { Icon } from 'antd';
import styled from 'styled-components';

/**
 * FilterPillsBar — Reusable filter pills bar for the TopBar advanced slot.
 *
 * See docs/ADVANCED_FILTERS.md for full patterns, best practices, and color standards.
 *
 * Usage:
 *   <FilterPillsBar
 *     pills={[
 *       { key: 'year', label: 'Ano: 2026', color: '#1890ff', icon: 'calendar' },
 *       { key: 'status', label: 'Ativos', color: '#a0d911', onRemove: function() { ... } },
 *     ]}
 *     onClearAll={function() { ... }}   // optional — shows "Limpar tudo" link
 *   />
 *
 * Each pill object:
 *   - key:       unique identifier (required)
 *   - label:     display text (required)
 *   - color:     pill color (required)
 *   - icon:      optional antd icon type (shows instead of dot)
 *   - onRemove:  optional callback — if provided, shows "x" button on pill
 */

var Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
`;

var Label = styled.span`
  font-size: 12px;
  color: #8c8c8c;
  margin-right: 2px;
`;

var ClearAll = styled.a`
  font-size: 12px;
  color: #8c8c8c;
  margin-left: 4px;
  cursor: pointer;

  &:hover {
    color: #f5222d;
  }
`;

var FilterPillsBar = function(props) {
  var pills = props.pills || [];
  var onClearAll = props.onClearAll;

  if (pills.length === 0) return null;

  return React.createElement(Bar, null,
    React.createElement(Label, null, 'Filtros:'),
    pills.map(function(pill) {
      return React.createElement(FilterPill, {
        key: pill.key,
        label: pill.label,
        color: pill.color,
        icon: pill.icon,
        onRemove: pill.onRemove,
      });
    }),
    onClearAll && React.createElement(ClearAll, { onClick: onClearAll }, 'Limpar tudo')
  );
};

/**
 * FilterPill — A single filter pill.
 *
 * Props:
 *   - label:     display text
 *   - color:     pill color
 *   - icon:      optional antd icon type (shows instead of colored dot)
 *   - onRemove:  optional callback — if provided, shows "x" close button
 */
var FilterPill = function(props) {
  var label = props.label;
  var color = props.color;
  var icon = props.icon;
  var onRemove = props.onRemove;

  var pillStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '3px 8px 3px 10px',
    borderRadius: 16,
    fontSize: 12,
    background: color + '15',
    color: color,
    border: '1px solid ' + color + '40',
    lineHeight: '20px',
  };

  var closeStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 16,
    height: 16,
    borderRadius: '50%',
    cursor: 'pointer',
    background: color + '20',
    color: color,
    fontSize: 9,
    lineHeight: 1,
    transition: 'all 0.15s',
  };

  var dotStyle = {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: color,
    flexShrink: 0,
  };

  return React.createElement('span', { style: pillStyle },
    icon
      ? React.createElement(Icon, { type: icon, style: { fontSize: 11 } })
      : React.createElement('span', { style: dotStyle }),
    label,
    onRemove && React.createElement('span', {
      style: closeStyle,
      onClick: function(e) { e.stopPropagation(); onRemove(); },
    },
      React.createElement(Icon, { type: 'close' })
    )
  );
};

export default FilterPillsBar;
export { FilterPill };
